import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/axios';
import { useAuth } from './AuthContext';

/**
 * Rules & Permissions for the signed-in user, loaded from GET /rules/me.
 *
 *   can('position.create')                 company-level action allowed?
 *   canAny('whatsapp.send')                allowed on at least some positions?
 *   canOn(position, 'resume.upload')       allowed on this position? (uses position.allowedActions
 *                                          from /positions/with-counts; falls back to canAny)
 *   policy('assign.default_access')        a company / team rule value
 *
 * The server enforces every rule; this only hides or disables buttons so people
 * aren't offered actions that would be refused.
 */
const PermissionsContext = createContext(null);

const OPEN = {
  loaded: false,
  permissions: {},
  policies: {},
  roles: [],
  unrestricted: false,
  canEditCompanyRules: false,
  canEditTeamRules: false,
};

export function PermissionsProvider({ children }) {
  const { user, tenantId, isLoading } = useAuth();
  const [me, setMe] = useState(OPEN);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/rules/me', { params: { _: Date.now() } });
      setMe({ ...OPEN, ...res.data, loaded: true });
    } catch {
      // Older backend or signed out: behave as before (server still enforces).
      setMe({ ...OPEN, loaded: true, unrestricted: true });
    }
  }, []);

  useEffect(() => {
    if (isLoading || !user) return;
    refresh();
  }, [isLoading, user, user?.email, tenantId, refresh]);

  const value = useMemo(() => {
    const scope = (key) => (me.unrestricted ? 'ALL' : me.permissions?.[key] || 'NONE');
    const can = (key) => scope(key) === 'ALL';
    const canAny = (key) => scope(key) !== 'NONE';
    const canOn = (position, key) => {
      if (me.unrestricted) return true;
      if (Array.isArray(position?.allowedActions)) return position.allowedActions.includes(key);
      return canAny(key);
    };
    const policy = (key) => me.policies?.[key];
    const policyOn = (key) => String(me.policies?.[key]) === 'true';
    const hasRole = (role) => Array.isArray(me.roles) && me.roles.includes(role);
    return { ...me, scope, can, canAny, canOn, policy, policyOn, hasRole, refresh };
  }, [me, refresh]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Outside the provider (e.g. public pages): allow, the server decides.
    return {
      ...OPEN, loaded: true, unrestricted: true,
      scope: () => 'ALL', can: () => true, canAny: () => true, canOn: () => true,
      policy: () => undefined, policyOn: () => false, hasRole: () => false, refresh: () => {},
    };
  }
  return ctx;
}

/**
 * What the signed-in user may do on one position: { allowedActions, myAccess, locked, can(key) }.
 * Until it loads, can() returns true so nothing flickers disabled; the server still enforces.
 */
export function usePositionAccess(positionId) {
  const [access, setAccess] = useState(null);
  useEffect(() => {
    if (!positionId) { setAccess(null); return undefined; }
    let alive = true;
    api.get(`/positions/${positionId}/my-access`, { params: { _: Date.now() } })
      .then((res) => { if (alive) setAccess(res.data); })
      .catch(() => { if (alive) setAccess(null); });
    return () => { alive = false; };
  }, [positionId]);
  return useMemo(() => ({
    ...(access || {}),
    loaded: !!access,
    can: (key) => !access || (Array.isArray(access.allowedActions) && access.allowedActions.includes(key)),
  }), [access]);
}

/** Reads the server's "why not" message from a 403 PERMISSION_DENIED response. */
export function permissionError(err, fallback) {
  const data = err?.response?.data;
  return data?.message || data?.error || fallback;
}

export default PermissionsContext;
